/**
 * Token-level diff for pattern text, where a multi-size group (`74 (82, 90, 98)`, `k1 (1, 2, 1)`,
 * `78-84-90`, `4 : 6 : 8`) is ONE token. Used to preview fixes and to summarize them per size.
 */

const NUM = String.raw`\d+(?:[.,]\d+)?`;
const GROUP = String.raw`[A-Za-z]*${NUM}\s*\(\s*${NUM}(?:\s*[,;]\s*${NUM})*\s*\)|\d+(?:-\d+){2,}|\d+(?:\s:\s\d+){2,}`;
const TOKEN_RE = new RegExp(String.raw`${GROUP}|\s+|[\p{L}\p{N}'’]+|\S`, 'gu');

export type DiffSeg = { kind: 'same' | 'removed' | 'added'; text: string };

export function tokenize(text: string): string[] {
  return text.match(TOKEN_RE) ?? [];
}

const key = (t: string) => (/^\s+$/.test(t) ? ' ' : t);

/** Longest-common-subsequence diff over tokens; adjacent segments of the same kind are merged. */
export function diffTokens(before: string, after: string): DiffSeg[] {
  const a = tokenize(before);
  const b = tokenize(after);
  const out: DiffSeg[] = [];
  const push = (kind: DiffSeg['kind'], text: string) => {
    if (!text) return;
    const last = out[out.length - 1];
    if (last && last.kind === kind) last.text += text;
    else out.push({ kind, text });
  };
  if (a.length * b.length > 250_000) {
    // Pathologically long lines: common prefix/suffix only.
    let p = 0;
    while (p < a.length && p < b.length && key(a[p]) === key(b[p])) p++;
    let s = 0;
    while (s < a.length - p && s < b.length - p && key(a[a.length - 1 - s]) === key(b[b.length - 1 - s])) s++;
    push('same', a.slice(0, p).join(''));
    push('removed', a.slice(p, a.length - s).join(''));
    push('added', b.slice(p, b.length - s).join(''));
    push('same', a.slice(a.length - s).join(''));
    return out;
  }
  const n = a.length;
  const m = b.length;
  const lcs: Uint16Array[] = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = key(a[i]) === key(b[j]) ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  let i = 0;
  let j = 0;
  let removed = '';
  let added = '';
  const flush = () => {
    push('removed', removed);
    push('added', added);
    removed = added = '';
  };
  while (i < n || j < m) {
    if (i < n && j < m && key(a[i]) === key(b[j])) {
      flush();
      push('same', b[j]);
      i++;
      j++;
    } else if (j < m && (i === n || lcs[i][j + 1] >= lcs[i + 1][j])) {
      added += b[j++];
    } else {
      removed += a[i++];
    }
  }
  flush();
  return out;
}

export interface NumericToken {
  prefix: string;
  values: string[];
}

/** `86` → {'' , [86]}, `k1 (1, 2, 1)` → {'k', [1,1,2,1]}, `78-84-90` → {'', [78,84,90]}. */
export function parseNumericToken(token: string): NumericToken | null {
  const t = token.trim().replace(/[.,;:]+$/, '');
  let m = new RegExp(String.raw`^([A-Za-z]*)(${NUM})(?:\s*\(\s*(${NUM}(?:\s*[,;]\s*${NUM})*)\s*\))?$`).exec(t);
  if (m) return { prefix: m[1].toLowerCase(), values: [m[2], ...(m[3] ? m[3].split(/\s*[,;]\s*/) : [])] };
  m = /^(\d+(?:-\d+){2,})$/.exec(t);
  if (m) return { prefix: '', values: m[1].split('-') };
  m = /^(\d+(?:\s:\s\d+){2,})$/.exec(t);
  if (m) return { prefix: '', values: m[1].split(/\s:\s/) };
  return null;
}

export type Change =
  | { kind: 'values'; size: string | null; from: string; to: string }
  | { kind: 'removed'; text: string }
  | { kind: 'added'; text: string }
  | { kind: 'replaced'; from: string; to: string };

/**
 * What a line edit changes, in knitting terms: per-size value changes when a number or a multi-size
 * group changed ("Adult S: 2 → 1"), otherwise the removed/added/replaced text. Deduplicated.
 */
export function summarizeChanges(before: string, after: string, sizeNames: readonly string[]): Change[] {
  const segs = diffTokens(before, after);
  const hunks: Array<{ removed: string; added: string }> = [];
  let cur: { removed: string; added: string } | null = null;
  for (const s of segs) {
    if (s.kind === 'same') {
      if (cur) hunks.push(cur);
      cur = null;
      continue;
    }
    cur ??= { removed: '', added: '' };
    cur[s.kind] += s.text;
  }
  if (cur) hunks.push(cur);

  const changes: Change[] = [];
  const seen = new Set<string>();
  const add = (c: Change) => {
    const k = JSON.stringify(c);
    if (!seen.has(k)) {
      seen.add(k);
      changes.push(c);
    }
  };
  const words = (s: string) => tokenize(s).filter((t) => t.trim() !== '');
  for (const h of hunks) {
    const r = words(h.removed);
    const a = words(h.added);
    const from = r.length === 1 ? parseNumericToken(r[0]) : null;
    const to = a.length === 1 ? parseNumericToken(a[0]) : null;
    if (from && to && from.prefix === to.prefix) {
      const count = Math.max(sizeNames.length, from.values.length, to.values.length);
      const expand = (v: string[]) => (v.length === 1 ? Array(count).fill(v[0]) : v);
      const fv = expand(from.values);
      const tv = expand(to.values);
      if (fv.length === tv.length) {
        const single = sizeNames.length <= 1 || (from.values.length === 1 && to.values.length === 1);
        fv.forEach((v, i) => {
          if (v !== tv[i]) add({ kind: 'values', size: single ? null : (sizeNames[i] ?? `#${i + 1}`), from: v, to: tv[i] });
        });
        continue;
      }
    }
    const rt = h.removed.trim();
    const at = h.added.trim();
    if (rt && !at) add({ kind: 'removed', text: rt });
    else if (at && !rt) add({ kind: 'added', text: at });
    else if (rt || at) add({ kind: 'replaced', from: rt, to: at });
  }
  return changes;
}
